import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks for fetching and managing transactions
export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('transactions');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const fetchTransactionById = createAsyncThunk(
  'transactions/fetchTransactionById',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response = await api.get(`transactions/${transactionId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const createTransactionFromReminder = createAsyncThunk(
  'transactions/createTransactionFromReminder',
  async (transactionData, { rejectWithValue }) => {
    try {
      const response = await api.post('transactions/from-reminder', transactionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const createDirectTransaction = createAsyncThunk(
  'transactions/createDirectTransaction',
  async (transactionData, { rejectWithValue }) => {
    try {
      const response = await api.post('transactions/direct', transactionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const updateTransaction = createAsyncThunk(
  'transactions/updateTransaction',
  async ({ transactionId, transactionData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`transactions/${transactionId}`, transactionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const deleteTransaction = createAsyncThunk(
  'transactions/deleteTransaction',
  async (transactionId, { rejectWithValue }) => {
    try {
      await api.delete(`transactions/${transactionId}`);
      return transactionId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const sendReceiptEmail = createAsyncThunk(
  'transactions/sendReceiptEmail',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response = await api.post(`transactions/${transactionId}/send-email`);
      return { transactionId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const sendReceiptWhatsApp = createAsyncThunk(
  'transactions/sendReceiptWhatsApp',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response = await api.post(`transactions/${transactionId}/send-whatsapp`);
      return { transactionId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const downloadReceiptPDF = createAsyncThunk(
  'transactions/downloadReceiptPDF',
  async ({ transactionId, receiptNumber }, { rejectWithValue }) => {
    try {
      const blobData = await api.get(`transactions/${transactionId}/receipt-pdf`, {
        responseType: 'blob',
      });

      // Create download link — blobData is already the raw blob from the response interceptor
      const url = window.URL.createObjectURL(new Blob([blobData], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${receiptNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return transactionId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

const transactionSlice = createSlice({
  name: 'transactions',
  initialState: {
    transactions: [],
    currentTransaction: null,
    loading: false,
    error: null,
    receiptSending: {
      email: false,
      whatsapp: false,
      pdf: false,
    },
    receiptError: null,
  },
  reducers: {
    // Clear error messages
    clearError: (state) => {
      state.error = null;
      state.receiptError = null;
    },

    // Clear current transaction
    clearCurrentTransaction: (state) => {
      state.currentTransaction = null;
    },

    // Update transaction locally (for optimistic updates)
    updateTransactionLocal: (state, action) => {
      const index = state.transactions.findIndex((t) => t.id === action.payload.id);
      if (index !== -1) {
        state.transactions[index] = { ...state.transactions[index], ...action.payload };
      }
    },

    // Update receipt status locally (for optimistic updates)
    updateReceiptStatusLocal: (state, action) => {
      const { transactionId, receiptType, sentAt } = action.payload;
      const transaction = state.transactions.find((t) => t.id === transactionId);

      if (transaction) {
        if (receiptType === 'email') {
          transaction.receipt_sent_via_email = true;
          transaction.receipt_email_sent_at = sentAt;
        } else if (receiptType === 'whatsapp') {
          transaction.receipt_sent_via_whatsapp = true;
          transaction.receipt_whatsapp_sent_at = sentAt;
        }
      }

      if (state.currentTransaction?.id === transactionId) {
        if (receiptType === 'email') {
          state.currentTransaction.receipt_sent_via_email = true;
          state.currentTransaction.receipt_email_sent_at = sentAt;
        } else if (receiptType === 'whatsapp') {
          state.currentTransaction.receipt_sent_via_whatsapp = true;
          state.currentTransaction.receipt_whatsapp_sent_at = sentAt;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch all transactions
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload;
        state.error = null;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch transaction by ID
    builder
      .addCase(fetchTransactionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTransaction = action.payload;
        state.error = null;
      })
      .addCase(fetchTransactionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create transaction from reminder
    builder
      .addCase(createTransactionFromReminder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransactionFromReminder.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions.push(action.payload);
        state.error = null;
      })
      .addCase(createTransactionFromReminder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create direct transaction
    builder
      .addCase(createDirectTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDirectTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions.push(action.payload);
        state.error = null;
      })
      .addCase(createDirectTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update transaction
    builder
      .addCase(updateTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransaction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.transactions.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.transactions[index] = action.payload;
        }
        if (state.currentTransaction?.id === action.payload.id) {
          state.currentTransaction = action.payload;
        }
        state.error = null;
      })
      .addCase(updateTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete transaction
    builder
      .addCase(deleteTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = state.transactions.filter((t) => t.id !== action.payload);
        if (state.currentTransaction?.id === action.payload) {
          state.currentTransaction = null;
        }
        state.error = null;
      })
      .addCase(deleteTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Send receipt via email
    builder
      .addCase(sendReceiptEmail.pending, (state) => {
        state.receiptSending.email = true;
        state.receiptError = null;
      })
      .addCase(sendReceiptEmail.fulfilled, (state, action) => {
        state.receiptSending.email = false;
        const { transactionId } = action.payload;
        const transaction = state.transactions.find((t) => t.id === transactionId);
        if (transaction) {
          transaction.receipt_sent_via_email = true;
          transaction.receipt_email_sent_at = new Date().toISOString();
        }
        if (state.currentTransaction?.id === transactionId) {
          state.currentTransaction.receipt_sent_via_email = true;
          state.currentTransaction.receipt_email_sent_at = new Date().toISOString();
        }
        state.receiptError = null;
      })
      .addCase(sendReceiptEmail.rejected, (state, action) => {
        state.receiptSending.email = false;
        state.receiptError = action.payload;
      });

    // Send receipt via WhatsApp
    builder
      .addCase(sendReceiptWhatsApp.pending, (state) => {
        state.receiptSending.whatsapp = true;
        state.receiptError = null;
      })
      .addCase(sendReceiptWhatsApp.fulfilled, (state, action) => {
        state.receiptSending.whatsapp = false;
        const { transactionId } = action.payload;
        const transaction = state.transactions.find((t) => t.id === transactionId);
        if (transaction) {
          transaction.receipt_sent_via_whatsapp = true;
          transaction.receipt_whatsapp_sent_at = new Date().toISOString();
        }
        if (state.currentTransaction?.id === transactionId) {
          state.currentTransaction.receipt_sent_via_whatsapp = true;
          state.currentTransaction.receipt_whatsapp_sent_at = new Date().toISOString();
        }
        state.receiptError = null;
      })
      .addCase(sendReceiptWhatsApp.rejected, (state, action) => {
        state.receiptSending.whatsapp = false;
        state.receiptError = action.payload;
      });

    // Download receipt PDF
    builder
      .addCase(downloadReceiptPDF.pending, (state) => {
        state.receiptSending.pdf = true;
        state.receiptError = null;
      })
      .addCase(downloadReceiptPDF.fulfilled, (state) => {
        state.receiptSending.pdf = false;
        state.receiptError = null;
      })
      .addCase(downloadReceiptPDF.rejected, (state, action) => {
        state.receiptSending.pdf = false;
        state.receiptError = action.payload;
      });
  },
});

export const {
  clearError,
  clearCurrentTransaction,
  updateTransactionLocal,
  updateReceiptStatusLocal,
} = transactionSlice.actions;

// Selectors
export const selectAllTransactions = (state) => state.transactions.transactions;
export const selectCurrentTransaction = (state) => state.transactions.currentTransaction;
export const selectTransactionsLoading = (state) => state.transactions.loading;
export const selectTransactionsError = (state) => state.transactions.error;
export const selectReceiptSending = (state) => state.transactions.receiptSending;
export const selectReceiptError = (state) => state.transactions.receiptError;

// Selector to get transaction by ID
export const selectTransactionById = (state, transactionId) => {
  return state.transactions.transactions.find((t) => t.id === transactionId);
};

// Selector to filter transactions by pledge
export const selectTransactionsByPledge = (state, pledgeId) => {
  return state.transactions.transactions.filter((t) => t.donation_id === pledgeId);
};

// Selector to filter transactions by contact
export const selectTransactionsByContact = (state, contactId) => {
  return state.transactions.transactions.filter((t) => t.contact_id === contactId);
};

// Selector to get direct transactions (no pledge)
export const selectDirectTransactions = (state) => {
  return state.transactions.transactions.filter((t) => !t.donation_id);
};

// Selector to get transactions with unsent email receipts
export const selectTransactionsWithUnsentEmailReceipts = (state) => {
  return state.transactions.transactions.filter((t) => !t.receipt_sent_via_email);
};

// Selector to get transactions with unsent WhatsApp receipts
export const selectTransactionsWithUnsentWhatsAppReceipts = (state) => {
  return state.transactions.transactions.filter((t) => !t.receipt_sent_via_whatsapp);
};

export default transactionSlice.reducer;
